import { Controller } from "@hotwired/stimulus"

const SECTION_SELECTOR = '[data-controller~="conditional-visibility"]'

/**
 * Generic controller for conditionally showing/hiding elements based on input values
 * 
 * Usage:
 * <div data-controller="conditional-visibility" 
 *      data-conditional-visibility-input-id-value="payment_method_select"
 *      data-conditional-visibility-show-value="credit_card">
 *   <!-- This div will only be visible when payment_method_select has value "credit_card" -->
 * </div>
 * 
 * Usage with radio button groups:
 * <div data-controller="conditional-visibility" 
 *      data-conditional-visibility-input-name-value="payment_method"
 *      data-conditional-visibility-show-value="credit_card">
 *   <!-- This div will be visible when any radio with name="payment_method" has value "credit_card" -->
 * </div>
 * 
 * Advanced usage with multiple values:
 * <div data-controller="conditional-visibility" 
 *      data-conditional-visibility-input-id-value="user_role"
 *      data-conditional-visibility-show-value="admin,manager">
 *   <!-- This div will be visible when user_role is "admin" OR "manager" -->
 * </div>
 * 
 * Options:
 * - data-conditional-visibility-clear-on-hide-value="false" (default: true) - Clear form values when hidden
 * - data-conditional-visibility-invert-value="true" (default: false) - Show when value does NOT match
 *
 * Sections can be nested: a nested section is only visible when its own
 * condition holds AND every ancestor section is visible.
 */
export default class extends Controller {
  static values = {
    inputId: String,      // ID of the input element to watch
    inputName: String,    // Name of the input group to watch (for radio buttons)
    show: String,         // Value(s) that should show this element (comma-separated for multiple)
    clearOnHide: Boolean, // Whether to clear form values when hiding (default: true)
    invert: Boolean       // Whether to invert the logic (show when NOT matching)
  }

  connect() {
    this.setupInputElement()
    if (!this.inputElement && !this.inputElements) {
      const identifier = this.hasInputIdValue ? this.inputIdValue : this.inputNameValue
      console.warn(`ConditionalVisibility: Could not find input element(s) with ${this.hasInputIdValue ? 'ID' : 'name'}: ${identifier}`)
      return
    }

    // Set up event listeners
    this.boundCheckVisibility = this.checkVisibility.bind(this)
    this.addEventListeners()

    // Set initial visibility
    this.checkVisibility()
  }

  disconnect() {
    this.removeEventListeners()
  }

  checkVisibility() {
    const currentValue = this.getInputValue().toLowerCase()
    const targetValues = this.showValue.toLowerCase().split(',').map(v => v.trim())

    // Add common synonyms for boolean values
    const expandedTargetValues = this.expandSynonyms(targetValues)

    const matches = this.invertValue
      ? !expandedTargetValues.includes(currentValue)
      : expandedTargetValues.includes(currentValue)
    const shouldShow = matches && !this.hiddenAncestor()

    if (shouldShow) {
      this.show()
    } else {
      this.hide()
    }
  }

  show() {
    this.element.classList.remove("hidden")
    // Enable this section's own form fields; direct child sections re-evaluate
    // their own condition (and recurse) so their fields stay disabled when hidden.
    this.ownInputs().forEach(input => {
      // Respect inputs that must stay disabled regardless of visibility (e.g. a frozen,
      // code-defined config field). Without this, showing a section would re-enable a
      // server-rendered `disabled` input on connect, making it editable again. Treat this as
      // an opt-out flag: any value other than an explicit "false" (including a bare/empty
      // attribute) keeps the input disabled, so a future call site can't silently misfire.
      if (input.dataset.keepDisabled !== undefined && input.dataset.keepDisabled !== "false") return
      input.disabled = false
    })
    this.childControllers().forEach(controller => controller.checkVisibility())
  }

  hide() {
    this.element.classList.add("hidden")

    if (this.clearOnHideValue) {
      this.clearFormValues()
    }
    // Disable all form fields when hiding (so they don't submit)
    const inputs = this.element.querySelectorAll('input, select, textarea')
    inputs.forEach(input => {
      input.disabled = true
    })
    this.childControllers().forEach(controller => controller.hide())
  }

  // Private methods
  nestedElements() {
    return Array.from(this.element.querySelectorAll(SECTION_SELECTOR))
  }

  // Nested sections whose nearest conditional-visibility ancestor is this one.
  childControllers() {
    return this.nestedElements()
      .filter(element => element.parentElement.closest(SECTION_SELECTOR) === this.element)
      .map(element => this.application.getControllerForElementAndIdentifier(element, "conditional-visibility"))
      .filter(Boolean)
  }

  hiddenAncestor() {
    let ancestor = this.element.parentElement?.closest(SECTION_SELECTOR)
    while (ancestor) {
      if (ancestor.classList.contains("hidden")) return ancestor
      ancestor = ancestor.parentElement?.closest(SECTION_SELECTOR)
    }
    return null
  }

  ownInputs() {
    const nested = this.nestedElements()
    return Array.from(this.element.querySelectorAll('input, select, textarea'))
      .filter(input => !nested.some(section => section.contains(input)))
  }

  setupInputElement() {
    if (this.hasInputIdValue) {
      // Single element by ID
      this.inputElement = document.getElementById(this.inputIdValue)
      this.inputElements = this.inputElement ? [this.inputElement] : []
    } else if (this.hasInputNameValue) {
      // Multiple elements by name (for radio groups)
      this.inputElements = Array.from(document.querySelectorAll(`input[name="${this.inputNameValue}"], select[name="${this.inputNameValue}"]`))
      this.inputElement = this.inputElements[0] // For compatibility
    }
  }

  addEventListeners() {
    if (this.inputElements) {
      this.inputElements.forEach(element => {
        element.addEventListener("change", this.boundCheckVisibility)
        element.addEventListener("input", this.boundCheckVisibility)
      })
    }
  }

  removeEventListeners() {
    if (this.inputElements && this.boundCheckVisibility) {
      this.inputElements.forEach(element => {
        element.removeEventListener("change", this.boundCheckVisibility)
        element.removeEventListener("input", this.boundCheckVisibility)
      })
    }
  }

  getInputValue() {
    if (this.hasInputNameValue) {
      // For radio button groups, find the checked one
      const checked = this.inputElements.find(input =>
        input.type === "radio" ? input.checked : input.selected || input.value
      )
      return checked ? checked.value : ""
    } else if (this.inputElement) {
      // For single elements
      if (this.inputElement.type === "checkbox") {
        return this.inputElement.checked ? "true" : "false"
      } else {
        return this.inputElement.value
      }
    }
    return ""
  }

  expandSynonyms(values) {
    const expanded = [...values]

    values.forEach(value => {
      switch (value) {
        case "yes":
          expanded.push("true", "1")
          break
        case "no":
          expanded.push("false", "0")
          break
        case "true":
          expanded.push("yes", "1")
          break
        case "false":
          expanded.push("no", "0")
          break
        case "1":
          expanded.push("true", "yes")
          break
        case "0":
          expanded.push("false", "no")
          break
      }
    })

    return [...new Set(expanded)] // Remove duplicates
  }

  clearFormValues() {
    const inputs = this.element.querySelectorAll('input, select, textarea')
    inputs.forEach(input => {
      if (input.type === 'checkbox' || input.type === 'radio') {
        input.checked = false
      } else if (input.tagName.toLowerCase() === 'select') {
        input.selectedIndex = -1
      } else {
        input.value = ''
      }
    })
  }

  // Default values
  get clearOnHideValue() {
    return this.hasClearOnHideValue ? super.clearOnHideValue : true
  }

  get invertValue() {
    return this.hasInvertValue ? super.invertValue : false
  }
}
